import { z } from 'zod'
import type { EmailTriggerKey } from './mail'

// ─── FO-156 (batch-31) — e-mail open-tracking (pixel) ───────────────────────
//
// Gedeelde bron voor: welke triggers trackbaar zijn, proxy-herkenning
// (Apple MPP / Google image-proxy), apparaat-afleiding uit de user-agent en de
// open-event-DTO's. Gebruikt door packages/mail (injectie-gate), de API
// (pixel-endpoint + uitbox) en de backoffice (weergave + instelling).

/**
 * Triggers waarop open-tracking aangezet KAN worden (v1: de klantmails voor
 * werkbon en factuur). Bewust een kleine allowlist: interne mails en
 * medewerker-mails zijn nooit trackbaar. Een offerte-trigger bestaat (nog)
 * niet als EmailTriggerKey; zodra die er komt kan hij hier bij.
 *
 * De daadwerkelijke aan/uit-stand per trigger is `EmailTrigger.trackOpens`
 * (DEFAULT UIT - AVG-beleidsbesluit); deze lijst begrenst alleen wat aan mag.
 */
export const TRACKBARE_OPEN_TRIGGERS = [
  'WERKBON_NAAR_KLANT',
  'FACTUUR_NAAR_KLANT',
  // batch-61 welkomstmail (FO-448) — klant-welkomstmail: "laatst geopend"
  // op de accountkaart. Alleen de KLANT-variant; de medewerker-welkomstmail
  // is een interne mail en blijft (zoals alle medewerker-mails) ontrackbaar.
  'KLANTPORTAAL_WELKOMSTMAIL_INLOG',
] as const satisfies readonly EmailTriggerKey[]
export type TrackbareOpenTrigger = (typeof TRACKBARE_OPEN_TRIGGERS)[number]

export function isTrackbareOpenTrigger(
  key: string | null | undefined,
): key is TrackbareOpenTrigger {
  return (TRACKBARE_OPEN_TRIGGERS as readonly string[]).includes(key ?? '')
}

// ─── Proxy-herkenning ────────────────────────────────────────────────────────

export const MAIL_OPEN_PROXY_HINTS = ['APPLE_PROXY', 'GOOGLE_PROXY'] as const
export type MailOpenProxyHint = (typeof MAIL_OPEN_PROXY_HINTS)[number]

export const MAIL_OPEN_PROXY_LABELS: Record<MailOpenProxyHint, string> = {
  APPLE_PROXY: 'via Apple-proxy (Mail Privacy Protection)',
  GOOGLE_PROXY: 'via Google-proxy (Gmail)',
}

/**
 * Herken bekende mail-proxy's aan de user-agent. Heuristiek, bewust
 * conservatief: alleen patronen die vrijwel zeker een proxy zijn.
 *
 * - Gmail laadt afbeeldingen via de Google image-proxy; die zet
 *   "GoogleImageProxy" (ggpht.com) in de user-agent.
 * - Apple Mail Privacy Protection prefetcht pixels via een Apple-proxy die de
 *   user-agent tot exact "Mozilla/5.0" stript.
 *
 * Een herkende proxy betekent: het IP is een datacenter-IP en het "open"
 * kan een automatische prefetch zijn - de UI toont dat als zodanig en
 * presenteert het nooit als het echte klant-IP.
 */
export function bepaalMailOpenProxyHint(
  userAgent: string | null | undefined,
): MailOpenProxyHint | null {
  if (!userAgent) return null
  if (userAgent.includes('GoogleImageProxy')) return 'GOOGLE_PROXY'
  if (/^Mozilla\/5\.0$/.test(userAgent.trim())) return 'APPLE_PROXY'
  return null
}

/**
 * Korte NL-omschrijving van het apparaat/mailprogramma achter een open-event,
 * afgeleid uit user-agent + proxy-hint. Indicatief (user-agents liegen); bij
 * een herkende proxy wint het proxy-label, want dan zegt de user-agent niets
 * over het echte apparaat van de klant.
 */
export function beschrijfMailOpenApparaat(
  userAgent: string | null | undefined,
  proxyHint: string | null | undefined,
): string {
  if (proxyHint === 'GOOGLE_PROXY') return 'Gmail (Google-proxy)'
  if (proxyHint === 'APPLE_PROXY') return 'Apple Mail (privacy-proxy)'
  const ua = userAgent ?? ''
  if (ua === '') return 'Onbekend apparaat'
  if (/outlook/i.test(ua)) return 'Outlook'
  if (/thunderbird/i.test(ua)) return 'Thunderbird'
  if (/iphone|ipad/i.test(ua)) return 'Apple Mail (iOS)'
  if (/macintosh|mac os x/i.test(ua)) return 'Apple Mail (macOS)'
  if (/android/i.test(ua)) return 'Android'
  if (/windows/i.test(ua)) return 'Windows'
  return 'Onbekend apparaat'
}

// ─── DTO's ───────────────────────────────────────────────────────────────────

/** Eén open-event (pixel-hit) voor de uitbox-slide-over. */
export const outgoingEmailOpenDtoSchema = z.object({
  id: z.string(),
  openedAt: z.string().datetime(),
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
  proxyHint: z.string().nullable(),
})
export type OutgoingEmailOpenDTO = z.infer<typeof outgoingEmailOpenDtoSchema>

/**
 * Open-status van de mails bij één bron-entiteit (werkbon/factuur), voor de
 * indicator op de detailpagina. Samenvatting over de VERZONDEN, getrackte
 * mails van die entiteit; `tracked=false` betekent: geen enkele verzonden mail
 * van deze entiteit had tracking aan (indicator verbergen).
 */
export const mailEntityOpenStatusSchema = z.object({
  tracked: z.boolean(),
  laatstVerzondenOp: z.string().datetime().nullable(),
  firstOpenedAt: z.string().datetime().nullable(),
  lastOpenedAt: z.string().datetime().nullable(),
  openCount: z.number().int(),
})
export type MailEntityOpenStatusDTO = z.infer<typeof mailEntityOpenStatusSchema>

/** Entiteit-typen waarvoor de open-status-indicator bestaat. */
export const MAIL_OPEN_STATUS_ENTITY_TYPES = ['Werkbon', 'Invoice'] as const
export type MailOpenStatusEntityType = (typeof MAIL_OPEN_STATUS_ENTITY_TYPES)[number]

// ─── Pixel-strip (weergave) ──────────────────────────────────────────────────

/**
 * Verwijder de tracking-pixel uit opgeslagen mail-HTML vóór weergave in de
 * backoffice. De "volledige inhoud"-modal rendert de bodyHtml in een iframe;
 * zonder strip zou elke interne preview de pixel laden en een vals open-event
 * van een STREEP-medewerker registreren. Matcht uitsluitend onze eigen
 * gegenereerde tag (herkenbaar aan data-sp-pixel).
 */
export function stripTrackingPixel(html: string): string {
  return html.replace(/<img[^>]*data-sp-pixel[^>]*\/?>/gi, '')
}
